import classNames from "classnames";
import clamp from "lodash/clamp";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import ReactResizeDetector from "react-resize-detector";
import ButtonSmily from "../../../atoms/ButtonSmily";
import cupImage from "./cup.png";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconArrowLeft } from "../../../../icons/arrow-left.svg";
import { ReactComponent as IconArrowRight } from "../../../../icons/arrow-right.svg";
import { ReactComponent as IconX } from "../../../../icons/x.svg";
import Page from "../../../molecules/customization/Page";
import PagePresentation from "../../../molecules/customization/PagePresentation";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import {
  getCoverDefinitions,
  getCoverFilteredPages,
  getNoCoverDefinitions,
} from "../../../../utils/customization/cover";
import { findColorDefinition } from "../../../../utils/customization/finders";
import { isMobileSize } from "../../../../utils/customization/device";
import "./style.scss";

const DEFAULT_PPMM = 1.5;
const BORDER_WIDTH = 6;
const MARGIN_WIDTH = 50;
const MARGIN_HEIGHT = isMobileSize() ? 168 : 248;

const CustomizationPreview: FunctionComponent = () => {
  const intl = useIntl();
  const { content, dispatch, template } = useCustomizationContext();
  const [displayedPageIndex, setDisplayedPageIndex] = useState<number>(0);
  const [ppmm, setPpmm] = useState<number>(DEFAULT_PPMM);

  const isLayflat = template.templateType === "book" && template.pageMode === "layflat";

  //Cover pages
  const coverDefinitions = getCoverDefinitions(template.pageDefinitions);

  const frontCoverTemplateDefinitions: TemplatePageDefinition[] = [];
  const spineTemplateDefinitions: TemplatePageDefinition[] = [];
  const backCoverDefinitions: TemplatePageDefinition[] = [];

  coverDefinitions.forEach((pageDefinition) => {
    if (pageDefinition.type === "front-cover") {
      frontCoverTemplateDefinitions.push(pageDefinition);
    }
    if (pageDefinition.type === "spine") {
      spineTemplateDefinitions.push(pageDefinition);
    }
    if (pageDefinition.type === "back-cover") {
      backCoverDefinitions.push(pageDefinition);
    }
  });

  const frontCoverPages = getCoverFilteredPages(content.pages, frontCoverTemplateDefinitions);
  const spinePages = getCoverFilteredPages(content.pages, spineTemplateDefinitions);
  const backCoverPages = getCoverFilteredPages(content.pages, backCoverDefinitions);

  // Content pages
  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const contentPages = getCoverFilteredPages(content.pages, noCoverPageDefinitions);

  const onResize = useCallback(
    (width: number | undefined, height: number | undefined) => {
      if (!width || !height) {
        setPpmm(DEFAULT_PPMM);
        return;
      }

      const availableWidth = clamp(width - MARGIN_WIDTH, 0, 1200);
      const availableHeight = clamp(height - MARGIN_HEIGHT, 0, 800);

      const ppmmWidth = availableWidth / (frontCoverTemplateDefinitions[0].width.value * 2);
      const ppmmHeight = availableHeight / frontCoverTemplateDefinitions[0].height.value;

      const ppmmMin = Math.min(ppmmWidth, ppmmHeight);

      setPpmm(clamp(ppmmMin, 0.5, 20));
    },
    [frontCoverTemplateDefinitions[0], setPpmm]
  );

  const pages = [spinePages[0], frontCoverPages[0], ...contentPages, backCoverPages[0]];

  const isFrontCover = displayedPageIndex === 0;
  const isBackCover = displayedPageIndex + 1 >= pages.length;
  const isCover = isFrontCover || isBackCover;

  const getDisplayedPages = (displayedPageIndex: number) => {
    return pages.filter((page, index) =>
      isLayflat && displayedPageIndex > 1
        ? index === displayedPageIndex
        : index === displayedPageIndex || index === displayedPageIndex + 1
    );
  };

  const handleClickClose = () => {
    dispatch({ type: "setDisplayPreview", opened: false });
  };

  const handleClickNavigation = (direction: "left" | "right") => {
    if (direction === "left" && displayedPageIndex > 0) {
      const increment = isLayflat && displayedPageIndex > 2 ? 1 : 2;
      setDisplayedPageIndex(displayedPageIndex - increment);
    }
    if (direction === "right" && !isBackCover) {
      const increment = isLayflat && displayedPageIndex > 1 ? 1 : 2;
      setDisplayedPageIndex(displayedPageIndex + increment);
    }
  };

  useEffect(() => {
    function handleKeyboardNavigation(event: KeyboardEvent) {
      if (event.key === "ArrowLeft") {
        handleClickNavigation("left");
      } else if (event.key === "ArrowRight") {
        handleClickNavigation("right");
      }
    }

    window.addEventListener("keydown", handleKeyboardNavigation);

    return function cleanup() {
      window.removeEventListener("keydown", handleKeyboardNavigation);
    };
  }, [handleClickNavigation]);

  let pageLabel;
  if (displayedPageIndex === 0) {
    pageLabel = intl.formatMessage({ id: "kustom_CoverContent_front" });
  } else if (isBackCover) {
    pageLabel = intl.formatMessage({ id: "kustom_CustomizationPreview_back" });
  } else {
    pageLabel = (
      <>
        {intl.formatMessage({ id: "kustom_PageList_Pages__book" })} {displayedPageIndex - 1}
        {!isLayflat && ` - ${displayedPageIndex}`}
      </>
    );
  }

  const defaultColorDefinition = findColorDefinition(
    frontCoverPages[0].backgroundColorKey,
    template.colorDefinitions
  );
  const borderStyle = `${BORDER_WIDTH}px solid ${defaultColorDefinition?.backgroundValue || ""}`;

  const bookHeight = frontCoverTemplateDefinitions[0].height.value * ppmm + BORDER_WIDTH * 2;

  return (
    <div className="CustomizationPreview">
      <ReactResizeDetector handleHeight={true} handleWidth={true} onResize={onResize} />
      <ButtonSmily iconPosition="alone" onClick={handleClickClose} size="s" type="primary">
        <IconX />
      </ButtonSmily>
      <div className="CustomizationPreview-title">
        {intl.formatMessage({ id: "kustom_CustomizationPreview_title" })}
      </div>
      <div className="CustomizationPreview-content">
        <div style={{ height: bookHeight }}>
          <div
            className={classNames("CustomizationPreview-double", {
              "CustomizationPreview-double-page": !isCover,
              "CustomizationPreview-double-back": isBackCover,
            })}
            style={{ border: !isCover ? borderStyle : "" }}
          >
            {getDisplayedPages(displayedPageIndex).map((page) => {
              if (!page) return;
              const pageKey = page.key;

              return (
                <div className="CustomizationPreview-item" key={pageKey}>
                  <PagePresentation
                    pageDefinitionKey={page.pageDefinitionKey}
                    ppmm={ppmm}
                    template={template}
                  >
                    <Page
                      content={content}
                      pageKey={pageKey}
                      ppmm={ppmm}
                      skipEvents={true}
                      template={template}
                    />
                  </PagePresentation>
                  <div className="CustomizationPreview-item-shadow" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="CustomizationPreview-navigation">
          <div style={{ visibility: displayedPageIndex === 0 ? "hidden" : "initial" }}>
            <ButtonSmily
              iconPosition="alone"
              onClick={() => handleClickNavigation("left")}
              size="s"
              type="tertiary"
            >
              <IconArrowLeft />
            </ButtonSmily>
          </div>
          <div className="CustomizationPreview-navigation-label"> {pageLabel}</div>
          <div style={{ visibility: isBackCover ? "hidden" : "initial" }}>
            <ButtonSmily
              iconPosition="alone"
              onClick={() => handleClickNavigation("right")}
              size="s"
              type="tertiary"
            >
              <IconArrowRight />
            </ButtonSmily>
          </div>
        </div>
      </div>
      <div className="CustomizationPreview-shadow" />
      <img className="CustomizationPreview-cup" src={cupImage} />
    </div>
  );
};

export default CustomizationPreview;
